import { useEffect, useRef, useState } from "react";

/**
 * Kaşe (stamp) builder — generates a circular PNG stamp from text inputs.
 * Emits onChange(dataUrl, data) with the composed PNG and the raw data.
 */
export default function StampBuilder({ initial = {}, onChange, color = "#0b4e82" }) {
  const [data, setData] = useState({
    full_name: initial.full_name || "",
    title: initial.title || "",
    chamber: initial.chamber || "",
    license_no: initial.license_no || "",
    city: initial.city || "İstanbul",
  });
  const canvasRef = useRef(null);

  const render = () => {
    const c = canvasRef.current;
    if (!c) return;
    const S = 320;
    c.width = S; c.height = S;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, S, S);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, S, S);

    const cx = S / 2, cy = S / 2;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    // Outer ring
    ctx.lineWidth = 5;
    ctx.beginPath(); ctx.arc(cx, cy, 150, 0, 2 * Math.PI); ctx.stroke();
    // Inner ring
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(cx, cy, 110, 0, 2 * Math.PI); ctx.stroke();

    // Curved top text: chamber + license
    const top = `${data.chamber || "ODA"} · SICIL ${data.license_no || "—"}`;
    drawCircularText(ctx, top, cx, cy, 128, -Math.PI / 2, true, color);
    // Curved bottom text: city
    const bottom = `${data.city || ""}`;
    if (bottom) drawCircularText(ctx, bottom, cx, cy, 128, Math.PI / 2, false, color);

    // Center lines
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "bold 22px Helvetica, Arial, sans-serif";
    ctx.fillStyle = color;
    const name = (data.full_name || "AD SOYAD").toUpperCase();
    const title = (data.title || "ÜNVAN").toUpperCase();
    ctx.fillText(name.slice(0, 22), cx, cy - 8);
    ctx.font = "14px Helvetica, Arial, sans-serif";
    ctx.fillText(title.slice(0, 26), cx, cy + 18);
    // Small star between rings
    ctx.font = "22px Helvetica, Arial, sans-serif";
    ctx.fillText("★", cx, cy + 70);

    onChange?.(c.toDataURL("image/png"), data);
  };

  useEffect(() => { render(); /* eslint-disable-next-line */ }, [data, color]);

  const field = (name, label) => (
    <label>{label}
      <input value={data[name]} onChange={(e) => setData((d) => ({ ...d, [name]: e.target.value }))}
             data-testid={`stamp-${name}`}/>
    </label>
  );

  return (
    <div className="stamp-wrap">
      <div className="stamp-inputs">
        {field("full_name", "Ad Soyad")}
        {field("title", "Ünvan (örn. Yangın Danışmanı)")}
        {field("chamber", "Oda (örn. TMMOB MMO)")}
        {field("license_no", "Sicil No")}
        {field("city", "Şehir")}
      </div>
      <div className="stamp-preview">
        <canvas ref={canvasRef} data-testid="stamp-canvas" style={{ width: 220, height: 220 }}/>
        <small>Önizleme otomatik güncellenir</small>
      </div>
    </div>
  );
}

function drawCircularText(ctx, text, cx, cy, radius, startAngle, clockwise, color) {
  const chars = text.split("");
  const angleStep = (clockwise ? 1 : -1) * (Math.PI * 0.78) / Math.max(chars.length - 1, 1);
  const startFrom = startAngle - (clockwise ? (chars.length - 1) * angleStep / 2 : -(chars.length - 1) * angleStep / 2);
  ctx.save();
  ctx.fillStyle = color;
  ctx.font = "bold 12px Helvetica, Arial, sans-serif";
  chars.forEach((ch, i) => {
    const a = startFrom + angleStep * i;
    const x = cx + radius * Math.cos(a);
    const y = cy + radius * Math.sin(a);
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(clockwise ? a + Math.PI / 2 : a - Math.PI / 2);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(ch, 0, 0);
    ctx.restore();
  });
  ctx.restore();
}
